import { isValid, addHours, isDate, format, addMinutes } from 'date-fns'

export const normalizeCurrency = (value: any, styled = false): string => {
  if (!value && value !== 0) {
    return value
  }

  const isNegative = Number(value) < 0

  if (Number(value) === 0) {
    return `${styled ? 'R$ ' : ''}0,00`
  }

  const parsedValue = Number(String(value).replace(/\D/g, '')) / 100

  if (styled) {
    return isNegative
      ? `- ${parsedValue.toLocaleString('pt-br', {
          style: 'currency',
          currency: 'BRL'
        })}`
      : parsedValue.toLocaleString('pt-br', {
          style: 'currency',
          currency: 'BRL'
        })
  }

  return isNegative
    ? `- ${parsedValue.toLocaleString('pt-br', {
        minimumFractionDigits: 2
      })}`
    : parsedValue.toLocaleString('pt-br', {
        minimumFractionDigits: 2
      })
}

export const normalizeDecimal = (value: any, precision?: number): string => {
  if (!value) {
    return value
  }

  precision = precision || 4

  const stringOfNumber = String(Number(normalizeNumber(value)))

  if (stringOfNumber === '0') {
    return '0'
  }

  if (stringOfNumber.length <= precision) {
    let result = '0.'
    const reversed = stringOfNumber.split('').reverse().join('')
    for (let i = precision - 1; i >= 0; i -= 1) {
      result += reversed[i] || '0'
    }
    return result
  }
  const cents = stringOfNumber.slice(
    stringOfNumber.length - precision,
    stringOfNumber.length
  )
  const reals = stringOfNumber.slice(0, stringOfNumber.length - precision)

  return `${reals}.${cents}`
}

export const normalizeNumberV2 = (
  value: string,
  precision?: number
): string => {
  if (!value) return ''

  // mantém apenas números, vírgula e ponto
  let cleaned = value.replace(/[^0-9.,]/g, '')

  // converte vírgula em ponto
  cleaned = cleaned.replace(',', '.')

  // divide em parte inteira e decimal
  const [intPart, decPart = ''] = cleaned.split('.')

  // se não tem ponto decimal, retorna só a parte inteira
  if (!cleaned.includes('.') || precision === undefined) {
    return intPart
  }

  // completa com zeros até atingir o tamanho `precision`
  let paddedDec = decPart.padEnd(precision, '0')

  console.log('paddedDec', paddedDec)

  // remove zeros à esquerda, mas garante pelo menos 1 dígito
  paddedDec = paddedDec.replace(/^0+/, '') || '0'

  // re-pad para manter o tamanho fixo de precision
  paddedDec = paddedDec.padEnd(precision, '0')

  console.log('paddedDec', paddedDec)

  return `${intPart}.${paddedDec}`
}

export const normalizeDecimalV2 = (value: any, precision = 14): string => {
  if (value === null || value === undefined || value === '') {
    return (0).toFixed(precision)
  }

  const normalized = String(value).replace(',', '.')

  if (!normalized.includes('.')) {
    const safePrecision = Math.min(precision, 14)
    return `${normalized}.${'0'.repeat(safePrecision)}`
  }

  const num = Number(normalized)
  if (isNaN(num)) {
    throw new Error(`Valor inválido: ${value}`)
  }

  const safePrecision = Math.min(precision, 14)
  const formatted = num.toFixed(safePrecision)

  return formatted
}

export const subString = (value: string, max = 100): string => {
  if (String(value).length > max) {
    return `${String(value).slice(0, max - 3)}...`
  }
  return value
}

// Trechos que não devem ficar visíveis quando exibirmos a Observação (MEMO)
// de um movimento de conciliação bancária.
const HIDDEN_MOVEMENT_MEMO_KEYWORDS = [
  'INTERNET INSTITUICAO',
  ' - INSTITUICAO DE PAG',
  'INSTITUICAO DE PAGAMENTO',
  'S.A',
  'INSTITUICAO',
  'INTERNET',
  'ANTECIPACAO',
  'PAGAMENTO',
  '-'
]

export const sanitizeMovementMemo = (value: string): string => {
  if (!value) {
    return value
  }

  let result = String(value)

  HIDDEN_MOVEMENT_MEMO_KEYWORDS.forEach(keyword => {
    const escapedKeyword = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    result = result.replace(new RegExp(escapedKeyword, 'gi'), '')
  })

  return result.replace(/\s{2,}/g, ' ').trim()
}

export const normalizePercentage = (
  value: string,
  locale = 'pt-br'
): string => {
  if (!value) {
    return value
  }

  const parsedValue = Number(value.replace(/\D/g, '')) / 100

  if (parsedValue > 10000) {
    return value
  }

  return parsedValue.toLocaleString(locale, {
    minimumFractionDigits: 2
  })
}
export const normalizeAccBank = (value: string): string => {
  if (!value) {
    return value
  }
  const onlyNums = value.replace(/\D/g, '')

  if (onlyNums.length === 1) {
    return onlyNums
  }

  return `${onlyNums.slice(0, onlyNums.length - 1)}-${onlyNums.slice(
    onlyNums.length - 1,
    onlyNums.length
  )}`
}

export const normalizeNumber = (value: string): string => {
  if (!value) {
    return value
  }

  const onlyNums = String(value).replace(/\D/g, '')

  return onlyNums
}

export const cleanCpfCnpj = (value: string): string =>
  String(value || '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')

export const normalizeNumbersAndSpecialCharacters = (value: string): string => {
  if (!value) {
    return value
  }

  const only = value.replace(/[^0-9-_/]/g, '')

  return only
}

export const normalizeCnpj = (value: string): string => {
  if (!value) {
    return value
  }

  const normalized = String(value)
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
  const base = normalized.slice(0, 12).replace(/[^A-Z0-9]/g, '')
  const dv = normalized
    .slice(12)
    .replace(/[^0-9]/g, '')
    .slice(0, 2)
  const cnpj = `${base}${dv}`

  if (cnpj.length <= 2) {
    return cnpj
  }
  if (cnpj.length <= 5) {
    return `${cnpj.slice(0, 2)}.${cnpj.slice(2, 5)}`
  }
  if (cnpj.length <= 8) {
    return `${cnpj.slice(0, 2)}.${cnpj.slice(2, 5)}.${cnpj.slice(5, 8)}`
  }
  if (cnpj.length <= 12) {
    return `${cnpj.slice(0, 2)}.${cnpj.slice(2, 5)}.${cnpj.slice(
      5,
      8
    )}/${cnpj.slice(8, 12)}`
  }
  return `${cnpj.slice(0, 2)}.${cnpj.slice(2, 5)}.${cnpj.slice(
    5,
    8
  )}/${cnpj.slice(8, 12)}-${cnpj.slice(12, 14)}`
}

export const normalizeCpf = (value: string): string => {
  if (!value) {
    return value
  }

  const onlyNums = value.replace(/[^\d]/g, '')
  if (onlyNums.length <= 3) {
    return onlyNums
  }
  if (onlyNums.length <= 6) {
    return `${onlyNums.slice(0, 3)}.${onlyNums.slice(3, 6)}`
  }
  if (onlyNums.length <= 9) {
    return `${onlyNums.slice(0, 3)}.${onlyNums.slice(3, 6)}.${onlyNums.slice(
      6,
      9
    )}`
  }
  return `${onlyNums.slice(0, 3)}.${onlyNums.slice(3, 6)}.${onlyNums.slice(
    6,
    9
  )}-${onlyNums.slice(9, 11)}`
}

export const normalizeCpfCnpj = (value: string): string => {
  if (!value) {
    return value
  }

  const normalized = String(value)
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
  const hasAlpha = /[A-Z]/.test(normalized)

  // CPF: mantém a regra atual, aceitando somente números.
  if (!hasAlpha && normalized.length <= 11) {
    const onlyNums = normalized.replace(/[^\d]/g, '')

    if (onlyNums.length <= 3) {
      return onlyNums
    }
    if (onlyNums.length <= 6) {
      return `${onlyNums.slice(0, 3)}.${onlyNums.slice(3, 6)}`
    }
    if (onlyNums.length <= 9) {
      return `${onlyNums.slice(0, 3)}.${onlyNums.slice(3, 6)}.${onlyNums.slice(
        6,
        9
      )}`
    }
    return `${onlyNums.slice(0, 3)}.${onlyNums.slice(3, 6)}.${onlyNums.slice(
      6,
      9
    )}-${onlyNums.slice(9, 11)}`
  }

  // Alpha é permitido somente quando o documento tiver 14 caracteres (CNPJ).
  const cnpjSource = hasAlpha
    ? normalized
    : normalized.length === 14
    ? normalized
    : normalized.replace(/[^0-9]/g, '')

  const base = cnpjSource.slice(0, 12).replace(/[^A-Z0-9]/g, '')
  const dv = cnpjSource
    .slice(12)
    .replace(/[^0-9]/g, '')
    .slice(0, 2)
  const cnpj = `${base}${dv}`

  if (cnpj.length <= 2) {
    return cnpj
  }
  if (cnpj.length <= 5) {
    return `${cnpj.slice(0, 2)}.${cnpj.slice(2, 5)}`
  }
  if (cnpj.length <= 8) {
    return `${cnpj.slice(0, 2)}.${cnpj.slice(2, 5)}.${cnpj.slice(5, 8)}`
  }
  if (cnpj.length <= 12) {
    return `${cnpj.slice(0, 2)}.${cnpj.slice(2, 5)}.${cnpj.slice(
      5,
      8
    )}/${cnpj.slice(8, 12)}`
  }
  return `${cnpj.slice(0, 2)}.${cnpj.slice(2, 5)}.${cnpj.slice(
    5,
    8
  )}/${cnpj.slice(8, 12)}-${cnpj.slice(12, 14)}`
}

export const normalizeNewDate = (value: string): string => {
  if (!value) {
    return value
  }

  return `${value.slice(6, 10)}-${value.slice(3, 5)}-${
    Number(value.slice(0, 2)) + 1
  }`
}

export const normalizeDate = (value: string): string => {
  if (!value) {
    return value
  }

  const formated = value
    .replace(/\D/g, '')
    .replace(/(\d{2})(\d)/, '$1/$2')
    .replace(/(\d{2})(\d)/, '$1/$2')
    .replace(/(\d{4})\d+?$/, '$1')

  return formated
}

export const normalizeMonthYear = (value: string): string => {
  if (!value) {
    return value
  }
  // format XX/XXXX

  const formated = value
    .replace(/\D/g, '')
    .replace(/(\d{2})(\d)/, '$1/$2')
    .replace(/(\d{4})\d+?$/, '$1')

  return formated
}

export const normalizeDateTime = (
  value: string,
  format?: 'datetime' | 'date'
): string => {
  if (!value) {
    return value
  }

  const onlyNums = value.replace(/\D/g, '')

  if (format === 'date') {
    return `${onlyNums.slice(6, 8)}/${onlyNums.slice(4, 6)}/${onlyNums.slice(
      0,
      4
    )}`
  } else {
    return `${onlyNums.slice(6, 8)}/${onlyNums.slice(4, 6)}/${onlyNums.slice(
      0,
      4
    )} ${onlyNums.slice(8, 10)}:${onlyNums.slice(10, 12)}:${onlyNums.slice(
      12,
      14
    )}`
  }
}

export const normalizeCep = (value: string): string => {
  if (!value) {
    return value
  }

  const onlyNums = value.replace(/[^\d]/g, '')
  if (onlyNums.length <= 5) {
    return onlyNums
  }
  return `${onlyNums.slice(0, 5)}-${onlyNums.slice(5, 8)}`
}

export const normalizePhone = (value: string): string => {
  if (!value) {
    return value
  }

  const onlyNums = value.replace(/[^\d]/g, '')

  if (onlyNums.length === 0) {
    return ''
  }

  if (onlyNums.length <= 2) {
    return `(${onlyNums.slice(0, 2)}`
  }

  if (onlyNums.length <= 6 && onlyNums.length > 2) {
    return `(${onlyNums.slice(0, 2)}) ${onlyNums.slice(2, 6)}`
  }

  if (onlyNums.length > 6 && onlyNums.length < 11) {
    return `(${onlyNums.slice(0, 2)}) ${onlyNums.slice(2, 6)}-${onlyNums.slice(
      6,
      10
    )}`
  }

  return `(${onlyNums.slice(0, 2)}) ${onlyNums.slice(2, 7)}-${onlyNums.slice(
    7,
    11
  )}`
}

export const normalizeCmc7 = (value: string): string => {
  const onlyNums = value.split('<').join('').split('>').join('')
  if (onlyNums.length === 0) {
    return ''
  }
  if (onlyNums.length <= 8) {
    return `${'<'}${onlyNums}`
  }
  if (onlyNums.length <= 18) {
    return `${'<'}${onlyNums.slice(0, 8)}${'<'}${onlyNums.slice(8, 18)}`
  }
  return `${'<'}${onlyNums.slice(0, 8)}${'<'}${onlyNums.slice(
    8,
    18
  )}${'>'}${onlyNums.slice(18, 31)}`
}

export const stringToDate = (value: any): Date | boolean => {
  if (!value) {
    return false
  }

  if (isDate(value)) {
    return value
  }

  if (value.length < 10) {
    return false
  }

  const d = value.split('/')

  const date = new Date(`${d[2]}-${d[1]}-${d[0]}`)

  if (isValid(date)) {
    return addHours(date, 3)
  }

  return false
}

export const dateIsValid = (value: string | Date): boolean => {
  if (!value) {
    return false
  }
  try {
    if (isDate(new Date(value))) {
      return true
    }
    return false
  } catch (err) {
    return false
  }
}

export const neutralizeDate = (value: Date): Date => {
  if (!value || !dateIsValid(value)) return value as Date

  const userDate = new Date(value)

  const userTimezoneOffset = userDate.getTimezoneOffset()
  const targetTimezoneOffset = 180

  if (userTimezoneOffset !== targetTimezoneOffset) {
    const diff = userTimezoneOffset - targetTimezoneOffset

    const convertedDate = addMinutes(userDate, diff)

    return convertedDate
  }

  return new Date(value)
}

export const formatDate = (value: any, hours?: boolean): string => {
  try {
    const d = dateIsValid(value)
    if (d) {
      if (!hours) {
        const iso = new Date(value).toISOString()
        const [year, month, day] = iso.split('T')[0]?.split('-')
        return `${day}/${month}/${year}`
      }
      const date = new Date(value).toLocaleString('pt-BR', {
        timeZone: 'America/Recife',
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: hours ? 'numeric' : undefined,
        minute: hours ? 'numeric' : undefined
      })

      return date
    }
    return ''
  } catch (err) {
    return ''
  }
}

export const formatDateWithSeconds = (value: any, hours?: boolean): string => {
  try {
    const d = dateIsValid(value)
    if (d) {
      if (!hours) {
        const iso = new Date(value).toISOString()
        const [year, month, day] = iso.split('T')[0]?.split('-')
        return `${day}/${month}/${year}`
      }
      const date = new Date(value).toLocaleString('pt-BR', {
        timeZone: 'America/Recife',
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: hours ? 'numeric' : undefined,
        minute: hours ? 'numeric' : undefined,
        second: hours ? 'numeric' : undefined
      })

      return date
    }
    return ''
  } catch (err) {
    return ''
  }
}

export const formatDateScr = (date: string): string => {
  if (date.length === 6) {
    return `${date.slice(4, 6)}/${date.slice(0, 4)}`
  }

  return date
}

export function formatCnpjRoot(root: string): string {
  if (root?.length === 8) {
    return `${root.slice(0, 2)}.${root.slice(2, 5)}.${root.slice(5)}`
  }
  return root
}

/**
 * Formata um valor monetário enquanto o usuário digita.
 * Aceita apenas dígitos (sem negativos), exibe no formato "1.234,56".
 * Limitado a 999.999.999.999,99.
 *
 * Uso no onChange:
 *   onChange={e => setVal(normalizeCurrencyInput(e.target.value))}
 *
 * Para obter o número: Number(val.replace(/\./g, '').replace(',', '.'))
 */
export const normalizeCurrencyInput = (value: string): string => {
  const onlyDigits = value.replace(/\D/g, '').slice(0, 14)
  if (!onlyDigits || onlyDigits === '0') return ''
  const parsed = Number(onlyDigits) / 100
  if (parsed > 999999999999.99) return ''
  return parsed.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })
}

export const normalizePlate = (value: string): string => {
  if (!value) return value

  const onlyAlphaNum = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase()
  if (onlyAlphaNum.length <= 3) {
    return onlyAlphaNum
  }
  return `${onlyAlphaNum.slice(0, 3)}-${onlyAlphaNum.slice(3, 7)}`
}

export function normalizeFileName(fileName: string): string {
  return fileName
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '-')
}
